import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { createPortal } from "react-dom"
import HOC from "./HOC"
import axios from "axios";
import BaseUrl from "../BaseUrl"
import "./side.css"
import swal from "sweetalert";
import { FiCalendar, FiCheck, FiChevronLeft, FiChevronRight, FiLayers, FiPlus, FiTarget, FiZap } from "react-icons/fi";

const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
]

const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

const padDatePart = (value) => String(value).padStart(2, "0")

const formatDateValue = (date) =>
    `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`

const parseDateValue = (value) => {
    if (!value) return null

    const [year, month, day] = value.split("-").map(Number)
    if (!year || !month || !day) return null

    return new Date(year, month - 1, day)
}

const formatDisplayDate = (value) => {
    const date = parseDateValue(value)
    if (!date) return "Select date"

    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    })
}

const getCalendarMonth = (value, fallbackValue) => {
    const date = parseDateValue(value) || parseDateValue(fallbackValue) || new Date()
    return new Date(date.getFullYear(), date.getMonth(), 1)
}

const DateField = ({ label, value, minDate, helperText, onChange, align = "left" }) => {
    const [isOpen, setIsOpen] = useState(false)
    const [viewMonth, setViewMonth] = useState(() => getCalendarMonth(value, minDate))
    const [popupPosition, setPopupPosition] = useState(null)
    const fieldRef = useRef(null)
    const popupRef = useRef(null)

    const updatePopupPosition = useCallback(() => {
        const field = fieldRef.current
        if (!field) return

        const rect = field.getBoundingClientRect()
        const popupWidth = window.innerWidth < 640 ? Math.min(window.innerWidth - 24, rect.width) : 352
        const estimatedPopupHeight = 430
        const spaceBelow = window.innerHeight - rect.bottom
        const top =
            spaceBelow < estimatedPopupHeight && rect.top > estimatedPopupHeight
                ? rect.top - estimatedPopupHeight - 12
                : rect.bottom + 12
        const alignedLeft = align === "right" ? rect.right - popupWidth : rect.left
        const left = Math.min(Math.max(alignedLeft, 12), window.innerWidth - popupWidth - 12)

        setPopupPosition({
            left,
            top: Math.max(top, 12),
            width: popupWidth,
        })
    }, [align])

    useEffect(() => {
        if (!isOpen) return undefined

        const handleOutsideClick = (event) => {
            if (
                !fieldRef.current?.contains(event.target) &&
                !popupRef.current?.contains(event.target)
            ) {
                setIsOpen(false)
            }
        }

        document.addEventListener("mousedown", handleOutsideClick)
        document.addEventListener("touchstart", handleOutsideClick)

        return () => {
            document.removeEventListener("mousedown", handleOutsideClick)
            document.removeEventListener("touchstart", handleOutsideClick)
        }
    }, [isOpen])

    useEffect(() => {
        if (isOpen) {
            setViewMonth(getCalendarMonth(value, minDate))
            updatePopupPosition()
        }
    }, [isOpen, minDate, updatePopupPosition, value])

    useEffect(() => {
        if (!isOpen) return undefined

        window.addEventListener("resize", updatePopupPosition)
        window.addEventListener("scroll", updatePopupPosition, true)

        return () => {
            window.removeEventListener("resize", updatePopupPosition)
            window.removeEventListener("scroll", updatePopupPosition, true)
        }
    }, [isOpen, updatePopupPosition])

    const calendarDays = useMemo(() => {
        const firstDay = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1)
        const gridStart = new Date(firstDay)
        gridStart.setDate(1 - firstDay.getDay())

        return Array.from({ length: 42 }, (_, index) => {
            const date = new Date(gridStart)
            date.setDate(gridStart.getDate() + index)
            return date
        })
    }, [viewMonth])

    const selectedDate = parseDateValue(value)
    const todayValue = formatDateValue(new Date())
    const selectedValue = selectedDate ? formatDateValue(selectedDate) : ""

    const moveMonth = (direction) => {
        setViewMonth((current) => new Date(current.getFullYear(), current.getMonth() + direction, 1))
    }

    const selectDate = (dateValue, disabled) => {
        if (disabled) return

        onChange(dateValue)
        setIsOpen(false)
    }

    const calendarPopup = isOpen && popupPosition ? (
        <div
            ref={popupRef}
            className="fixed rounded-[1.7rem] border border-white/80 bg-white/95 p-3.5 shadow-2xl shadow-sky-950/20 ring-1 ring-sky-100/80 backdrop-blur-xl animate-[bsi-modal-pop_180ms_ease-out]"
            style={{
                left: popupPosition.left,
                top: popupPosition.top,
                width: popupPosition.width,
                zIndex: 9999,
            }}
        >
            <div className="pointer-events-none absolute inset-x-5 top-0 h-px bg-gradient-to-r from-transparent via-sky-300/80 to-transparent" />
            <div className="mb-3 flex items-center justify-between rounded-2xl bg-gradient-to-r from-sky-50 to-blue-50 px-2 py-2 ring-1 ring-sky-100">
                <button
                    type="button"
                    onClick={() => moveMonth(-1)}
                    className="flex h-9 w-9 items-center justify-center rounded-xl bg-white text-sky-700 shadow-sm ring-1 ring-sky-100 transition hover:-translate-x-0.5 hover:bg-sky-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-sky-300"
                    aria-label="Previous month"
                >
                    <FiChevronLeft className="h-5 w-5" />
                </button>
                <div className="text-center">
                    <p className="text-sm font-black text-slate-950">
                        {monthNames[viewMonth.getMonth()]} {viewMonth.getFullYear()}
                    </p>
                    <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-sky-600">
                        Goal calendar
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => moveMonth(1)}
                    className="flex h-9 w-9 items-center justify-center rounded-xl bg-white text-sky-700 shadow-sm ring-1 ring-sky-100 transition hover:translate-x-0.5 hover:bg-sky-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-sky-300"
                    aria-label="Next month"
                >
                    <FiChevronRight className="h-5 w-5" />
                </button>
            </div>

            <div className="grid grid-cols-7 gap-1.5 px-1 pb-1">
                {weekDays.map((day) => (
                    <div key={day} className="py-1 text-center text-[10px] font-black uppercase tracking-[0.14em] text-slate-400">
                        {day}
                    </div>
                ))}
                {calendarDays.map((date) => {
                    const dateValue = formatDateValue(date)
                    const isCurrentMonth = date.getMonth() === viewMonth.getMonth()
                    const isSelected = dateValue === selectedValue
                    const isToday = dateValue === todayValue
                    const isDisabled = Boolean(minDate && dateValue < minDate)

                    return (
                        <button
                            key={dateValue}
                            type="button"
                            disabled={isDisabled}
                            onClick={() => selectDate(dateValue, isDisabled)}
                            className={`relative flex h-10 items-center justify-center rounded-2xl text-sm font-extrabold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-sky-300 ${
                                isSelected
                                    ? "bg-gradient-to-br from-sky-500 to-blue-700 text-white shadow-lg shadow-sky-500/30"
                                    : isDisabled
                                        ? "cursor-not-allowed bg-slate-50 text-slate-300"
                                        : isCurrentMonth
                                            ? "text-slate-700 hover:-translate-y-0.5 hover:bg-sky-50 hover:text-sky-700 hover:shadow-md hover:shadow-sky-900/10"
                                            : "text-slate-300 hover:bg-slate-50"
                            }`}
                        >
                            {date.getDate()}
                            {isToday && !isSelected ? (
                                <span className="absolute bottom-1.5 h-1 w-1 rounded-full bg-sky-500" />
                            ) : null}
                        </button>
                    )
                })}
            </div>
        </div>
    ) : null

    return (
        <div ref={fieldRef} className="relative">
            <button
                type="button"
                onClick={() => {
                    setIsOpen((open) => !open)
                    window.requestAnimationFrame(updatePopupPosition)
                }}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault()
                        setIsOpen((open) => !open)
                        window.requestAnimationFrame(updatePopupPosition)
                    }
                }}
                aria-expanded={isOpen}
                className={`group flex w-full items-center gap-3 rounded-[1.35rem] border bg-gradient-to-br from-white via-white to-sky-50/80 p-3 text-left shadow-lg shadow-sky-900/5 ring-1 ring-white/80 transition-all duration-200 hover:-translate-y-0.5 hover:border-sky-300 hover:shadow-xl hover:shadow-sky-900/10 focus:outline-none focus:ring-4 focus:ring-sky-100 ${
                    isOpen ? "border-sky-400 shadow-xl shadow-sky-900/10 ring-sky-100" : "border-slate-200"
                }`}
            >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-blue-700 text-white shadow-lg shadow-sky-500/25 transition group-hover:scale-105">
                    <FiCalendar className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1">
                    <span className="block text-[11px] font-extrabold uppercase tracking-[0.18em] text-slate-500">
                        {label}
                    </span>
                    <span className={`mt-1 block truncate text-sm font-extrabold ${value ? "text-slate-950" : "text-slate-400"}`}>
                        {formatDisplayDate(value)}
                    </span>
                    <span className="mt-1 block truncate text-[11px] font-semibold text-sky-600">
                        {helperText}
                    </span>
                </span>
            </button>

            {calendarPopup ? createPortal(calendarPopup, document.body) : null}
        </div>
    )
}

const SkillCard = ({ item, selected, onToggle }) => (
    <button
        type="button"
        onClick={onToggle}
        aria-pressed={selected}
        className={`group relative min-h-[138px] overflow-hidden rounded-2xl border p-3 text-center shadow-sm transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-sky-100 ${
            selected
                ? "border-blue-300 bg-gradient-to-br from-sky-500 via-blue-600 to-blue-800 text-white shadow-2xl shadow-sky-500/30"
                : "border-slate-200 bg-gradient-to-br from-white via-white to-sky-50/80 text-slate-950 shadow-sky-900/5 hover:-translate-y-1 hover:border-sky-300 hover:shadow-2xl hover:shadow-sky-900/10"
        }`}
    >
        <span className={`pointer-events-none absolute left-0 top-0 h-1 w-full ${selected ? "bg-white/80" : "bg-gradient-to-r from-sky-400 via-blue-500 to-cyan-400"}`} />
        <span className={`pointer-events-none absolute -right-7 -top-7 h-20 w-20 rounded-full blur-2xl transition ${selected ? "bg-white/25" : "bg-sky-300/35 group-hover:bg-sky-400/40"}`} />
        <span
            className={`absolute right-2.5 top-2.5 z-10 flex h-6 w-6 items-center justify-center rounded-full text-sm shadow-md ring-1 transition ${
                selected
                    ? "bg-white text-blue-700 ring-white/80"
                    : "bg-white text-slate-400 ring-slate-200 group-hover:text-blue-700 group-hover:ring-sky-300"
            }`}
        >
            <FiCheck className="h-3 w-3" />
        </span>
        <span
            className={`relative mx-auto flex h-[70px] w-[70px] items-center justify-center rounded-2xl shadow-lg ring-2 transition-all duration-200 ${
                selected
                    ? "bg-gradient-to-br from-white to-sky-100 ring-white/35 shadow-blue-950/25"
                    : "bg-gradient-to-br from-sky-500 via-blue-600 to-blue-800 ring-sky-100 shadow-blue-700/25 group-hover:scale-105 group-hover:ring-sky-200 group-hover:shadow-blue-700/35"
            }`}
        >
            <span className={`absolute inset-1.5 rounded-xl transition ${selected ? "bg-gradient-to-br from-sky-500 to-blue-700" : "bg-white/12 group-hover:bg-white/18"}`} />
            <span className="absolute inset-0 rounded-2xl bg-white/10 opacity-0 transition group-hover:opacity-100" />
            <img
                alt={item.skill}
                className="relative z-10 h-9 w-9 object-contain transition-transform duration-200 group-hover:scale-110 sm:h-11 sm:w-11"
                src={BaseUrl() + item.image}
                style={{ filter: "brightness(0) invert(1) contrast(1.25) drop-shadow(0 10px 14px rgba(2, 6, 23, 0.28))" }}
            />
        </span>
        <span className="mt-3 block text-[13px] font-extrabold leading-4 tracking-tight sm:text-sm">{item.skill}</span>
        <span className={`mx-auto mt-1.5 inline-flex rounded-full px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.14em] ring-1 ${
            selected
                ? "bg-white/15 text-white ring-white/25"
                : "bg-sky-50 text-sky-700 ring-sky-100 group-hover:bg-white group-hover:text-blue-700"
        }`}>
            {selected ? "Selected" : "Tap to select"}
        </span>
    </button>
)

const SkillCardSkeleton = () => (
    <div className="min-h-[138px] rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-sky-50/70 p-3 shadow-sm">
        <div className="mx-auto h-[70px] w-[70px] rounded-2xl bg-sky-100 bsi-skeleton" />
        <div className="mx-auto mt-3 h-4 w-24 rounded-full bg-sky-100 bsi-skeleton" />
        <div className="mx-auto mt-2 h-4 w-20 rounded-full bg-sky-50 bsi-skeleton" />
    </div>
)

const SkillSection = ({ title, description, icon, skills, isSkillSelected, toggleSkill, loading }) => (
    <section className="rounded-3xl border border-white/80 bg-white/95 p-3.5 shadow-xl shadow-sky-900/5 backdrop-blur sm:p-4">
        <div className="mb-3 flex flex-col gap-2.5 border-b border-slate-100 pb-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-2.5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 to-blue-700 p-2.5 text-white shadow-lg shadow-sky-500/20 ring-1 ring-sky-200/80">
                    {icon}
                </div>
                <div>
                    <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-sky-600">{title}</p>
                    <h3 className="mt-0.5 text-lg font-extrabold tracking-tight text-slate-950">{description}</h3>
                </div>
            </div>
            <span className="inline-flex w-fit rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-extrabold text-sky-700 ring-1 ring-sky-100">
                {loading ? "Loading..." : `${skills.length} options`}
            </span>
        </div>

        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
            {loading ? (
                Array.from({ length: 6 }).map((_, index) => <SkillCardSkeleton key={index} />)
            ) : skills.map((item) => (
                <SkillCard
                    key={item._id}
                    item={item}
                    selected={isSkillSelected(item._id)}
                    onToggle={() => toggleSkill(item._id)}
                />
            ))}
        </div>
    </section>
)

const AddGoal = () => {
    const [softSkill, setSoftSkill] = useState([]);
    const [hardSkill, setHardSkill] = useState([]);
    const [startDate, setStartDate] = useState("")
    const [endDate, setEndDate] = useState("")
    const [selectedSkillIds, setSelectedSkillIds] = useState([])
    const [dateError, setDateError] = useState("")
    const [isLoadingSkills, setIsLoadingSkills] = useState(true)

    const token = localStorage.getItem("authorization")
    var auth = {
        headers: {
            authorization: `bearer ${token}`,
        },
    };

    const resetForm = useCallback(() => {
        setStartDate("")
        setEndDate("")
        setDateError("")
        setSelectedSkillIds([])
    }, [])

    const toggleSkill = (skillId) => {
        setSelectedSkillIds((prev) => {
            if (prev.includes(skillId)) {
                return prev.filter((id) => id !== skillId)
            }
            return [...prev, skillId]
        })
    }

    const isSkillSelected = (skillId) => selectedSkillIds.includes(skillId)

    const handleStartDateChange = (value) => {
        setStartDate(value)
        setDateError("")

        if (endDate && value && endDate < value) {
            setEndDate("")
            setDateError("End date was cleared because it cannot be earlier than the start date.")
        }
    }

    const handleEndDateChange = (value) => {
        if (startDate && value && value < startDate) {
            setEndDate("")
            setDateError("End date cannot be earlier than the selected start date.")
            swal("End date cannot be earlier than the selected start date.")
            return
        }

        setEndDate(value)
        setDateError("")
    }

    const addGoal = async () => {
        const data = {
            start_Date: startDate,
            end_Date: endDate,
            skilId: selectedSkillIds
        }
        if (data.skilId.length === 0) {
            swal("Please select at least one skill")
            return
        }
        if (data.start_Date === "") {
            swal("Please select a start date")
            return
        }
        if (data.end_Date === "") {
            swal("Please select an end date")
            return
        }
        if (data.start_Date && data.end_Date && data.end_Date < data.start_Date) {
            setDateError("End date cannot be earlier than the selected start date.")
            swal("End date cannot be earlier than the selected start date.")
            return
        }
        try {
            const url = BaseUrl() + "api/goal/add-goal"
            const res = await axios.post(url, data, auth)

            if (res.data.ResponseCode === 200) {
                resetForm()
                swal(res.data.ResponseMessage, {
                    icon: "success"
                })
            } else {
                swal("Something went wrong. Please try again.")
            }
        } catch (error) {
            console.log(error)
        }
    }

    const getAllSkill = async () => {
        const url = BaseUrl() + "api/skill/get-skill";
        setIsLoadingSkills(true)
        try {
            const res = await axios.get(url);
            if (res.data.ResponseCode === 200) {
                setSoftSkill(res.data.Response.soft_skill);
                setHardSkill(res.data.Response.hard_skill);
            }
        } catch (error) {
            console.log(error)
            throw error
        } finally {
            setIsLoadingSkills(false)
        }
    };

    useEffect(() => {
        getAllSkill();
    }, []);


    return (
        <main className="mx-auto flex w-full max-w-7xl flex-col gap-4">
            <section className="overflow-hidden rounded-3xl border border-white/70 bg-gradient-to-br from-sky-500 via-blue-600 to-blue-800 p-4 text-white shadow-2xl shadow-sky-900/20 sm:p-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div className="max-w-2xl">
                        <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-sky-100 ring-1 ring-white/20">
                            <FiTarget className="h-4 w-4" />
                            Goal Builder
                        </div>
                        <h2 className="text-2xl font-bold tracking-tight">Create a new goal</h2>
                        <p className="mt-1.5 max-w-xl text-sm leading-5 text-sky-100">
                            Choose a time period, select the skills you want to improve, and launch a focused development goal.
                        </p>
                    </div>

                    <div className="grid gap-2 rounded-2xl bg-white/15 p-3 ring-1 ring-white/20 backdrop-blur sm:min-w-[210px]">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-100">Selected Skills</p>
                        <div className="flex items-end justify-between gap-4">
                            <span className="text-2xl font-bold">{selectedSkillIds.length}</span>
                            <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-bold text-blue-700">Ready to add</span>
                        </div>
                    </div>
                </div>
            </section>

            <section className="rounded-3xl border border-white/80 bg-white/90 p-3.5 shadow-xl shadow-sky-900/5 backdrop-blur sm:p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex items-start gap-2.5">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-sky-100 to-blue-100 text-sky-700 ring-1 ring-sky-200/80">
                            <FiCalendar className="h-4 w-4" />
                        </div>
                        <div>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-sky-600">Select time period</p>
                            <h3 className="mt-0.5 text-base font-bold text-slate-900">Schedule your goal window</h3>
                            <p className="mt-1 text-sm text-slate-500">Set a clear start and end date for focused progress.</p>
                        </div>
                    </div>

                    <div className="grid w-full gap-3 sm:grid-cols-2 lg:max-w-2xl">
                        <DateField
                            label="Start Date"
                            value={startDate}
                            helperText="Choose when progress begins"
                            onChange={handleStartDateChange}
                        />

                        <DateField
                            label="End Date"
                            value={endDate}
                            minDate={startDate || undefined}
                            helperText={startDate ? "Must be on or after start" : "Select start date first"}
                            onChange={handleEndDateChange}
                            align="right"
                        />
                        {dateError ? (
                            <p className="sm:col-span-2 rounded-2xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700 ring-1 ring-amber-100">
                                {dateError}
                            </p>
                        ) : startDate ? (
                            <p className="sm:col-span-2 rounded-2xl bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-700 ring-1 ring-sky-100">
                                End date must be on or after {startDate}.
                            </p>
                        ) : null}
                    </div>
                </div>
            </section>

            <SkillSection
                title="Hard Skills"
                description="Technical capabilities"
                icon={<FiZap className="h-5 w-5" />}
                skills={hardSkill}
                isSkillSelected={isSkillSelected}
                toggleSkill={toggleSkill}
                loading={isLoadingSkills}
            />

            <SkillSection
                title="Soft Skills"
                description="Professional strengths"
                icon={<FiLayers className="h-5 w-5" />}
                skills={softSkill}
                isSkillSelected={isSkillSelected}
                toggleSkill={toggleSkill}
                loading={isLoadingSkills}
            />

            <div className="sticky bottom-3 z-10 flex justify-center">
                <div className="flex w-full max-w-7xl flex-col gap-2.5 rounded-2xl border border-white/80 bg-white/90 p-3 shadow-2xl shadow-sky-900/10 backdrop-blur-xl sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm font-bold text-slate-900">{selectedSkillIds.length} skills selected</p>
                        <p className="text-xs text-slate-500">Complete the dates and submit your goal.</p>
                    </div>
                    <button
                        type="button"
                        onClick={addGoal}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-sky-500 to-blue-700 px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-sky-600/20 transition hover:-translate-y-0.5 hover:shadow-xl hover:shadow-sky-700/25 focus:outline-none focus:ring-4 focus:ring-sky-100"
                    >
                        <FiPlus className="h-5 w-5" />
                        Add Goal
                    </button>
                </div>
            </div>
        </main>
    )
}
export default HOC(AddGoal)
